import { useState } from 'react'
import type { WizardLink } from './useWizardState'

interface Props {
  links: WizardLink[]
  onAdd: (link: WizardLink) => void
  onRemove: (index: number) => void
}

export function WizardStepLinks({ links, onAdd, onRemove }: Props) {
  const [title, setTitle] = useState('')
  const [url, setUrl] = useState('')

  function handleAdd() {
    if (!title.trim() || !url.trim()) return
    onAdd({ title: title.trim(), url: url.trim() })
    setTitle('')
    setUrl('')
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
        Add some starter links
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Optionally add a few links to get started. You can add more later.
      </p>

      {/* Add Link Form */}
      {links.length < 5 && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6 max-w-lg">
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Link title"
            maxLength={100}
            className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent text-sm"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <input
            type="text"
            value={url}
            onChange={e => setUrl(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-[#0d121b] text-gray-900 dark:text-white focus:ring-2 focus:ring-[#2B6CEE] focus:border-transparent text-sm"
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
          <button
            onClick={handleAdd}
            disabled={!title.trim() || !url.trim()}
            className="px-5 py-2.5 bg-[#2B6CEE] text-white text-sm font-medium rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
          >
            Add
          </button>
        </div>
      )}

      {/* Link List */}
      {links.length > 0 ? (
        <div className="space-y-2 max-w-lg">
          {links.map((link, i) => (
            <div
              key={i}
              className="flex items-center gap-3 px-4 py-3 bg-white dark:bg-[#1c2230] rounded-lg border border-gray-200 dark:border-white/10"
            >
              <span className="material-symbols-outlined text-gray-400 text-[18px]">link</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{link.title}</p>
                <p className="text-xs text-gray-400 truncate">{link.url}</p>
              </div>
              <button
                onClick={() => onRemove(i)}
                className="text-gray-400 hover:text-red-500 transition-colors"
              >
                <span className="material-symbols-outlined text-[18px]">close</span>
              </button>
            </div>
          ))}
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
            {links.length}/5 links added
          </p>
        </div>
      ) : (
        <div className="text-center py-10 bg-gray-50 dark:bg-white/5 rounded-xl border border-dashed border-gray-300 dark:border-white/10 max-w-lg">
          <span className="material-symbols-outlined text-3xl text-gray-300 dark:text-gray-600 mb-2 block">link_off</span>
          <p className="text-sm text-gray-400 dark:text-gray-500">
            No links yet — skip this step or add them later.
          </p>
        </div>
      )}
    </div>
  )
}
